/**
 * Authoritative reserved-slug registry for the flat tenant URL model (IBA-155).
 *
 * URLs collapsed from `/w/:ws/b/:branch` + `/w/:ws/organization` to:
 *   - `/:workspaceSlug`            → Organization context (SYSTEM/parish scope)
 *   - `/:workspaceSlug/:branchSlug` → Branch context
 *
 * Because both contexts share the top-level path namespace, two collisions must be guarded:
 *
 * 1. A **workspace slug** occupies a single top-level segment (`/:workspaceSlug`), so it can never equal a
 *    reserved top-level route word (`/login`, `/dashboard`, …). See {@link RESERVED_WORKSPACE_SLUGS}.
 * 2. A **branch slug** is the second segment (`/:workspaceSlug/:branchSlug`), which shares that slot with the
 *    Organization sub-routes (`/:ws/finance`, `/:ws/branches`, …). So a branch can never be named after an
 *    Organization/admin route, and the URL parser uses this set to decide whether `/acme/finance` means the
 *    Organization Finance page (branch → parish) or a branch literally named "finance".
 *    See {@link RESERVED_BRANCH_SLUGS}.
 *
 * This is the single source of truth consumed by:
 *   - the URL parser/builder ({@link file://./branch-admin-routing.ts})
 *   - the router reserved-path checks
 *   - branch/workspace creation validation (mirrored on the backend)
 *
 * IMPORTANT: {@link RESERVED_BRANCH_SLUGS} must stay in sync with the first path segment of every admin route
 * mounted under the Organization/branch shell (`createAdminBranchChildren` in `app/router/route-groups.tsx`).
 * A dedicated test asserts this coupling — if you add a top-level admin route, add its segment here too.
 */

/** Parish/organization URL alias — resolves to the workspace SYSTEM branch (ADR-0002). */
export const PARISH_BRANCH_SLUG = "parish"

/**
 * Top-level route words a workspace slug must never equal. Sourced from the router table
 * (`createTenantRootChildren`) and the public/auth route sets (`app-route-scope.ts`), plus the retired `w`/`b`
 * prefixes (kept reserved so legacy redirects keep resolving).
 */
export const RESERVED_WORKSPACE_SLUGS: ReadonlySet<string> = new Set([
  // Auth + onboarding
  "login",
  "register",
  "auth",
  "accept-invitation",
  "create-church",
  "register-church",
  "account-blocked",
  // App utility / entry pages
  "dashboard",
  "components-showcase",
  "form-builder",
  "no-access",
  "not-found",
  "subscription",
  "workspaces",
  "workspace-selector",
  "profile",
  // Marketing / public (served by the website but reserved here to avoid ambiguity)
  "about",
  "berger",
  "branches",
  "contact",
  "donation",
  "pricing",
  "programs",
  "prayer-request",
  // Retired URL prefixes (legacy redirects) + tenant base path
  "w",
  "b",
  "tenant",
])

/**
 * Second-segment names owned by the Organization/admin shell. A branch slug must never equal one of these, and
 * the parser treats `/:ws/<one-of-these>` as the Organization context (branch → {@link PARISH_BRANCH_SLUG}).
 *
 * Mirrors the first path segment of every child in `createAdminBranchChildren`.
 */
export const RESERVED_BRANCH_SLUGS: ReadonlySet<string> = new Set([
  PARISH_BRANCH_SLUG,
  "audit-log",
  "recent-activity",
  "branches",
  "ministries",
  "ministers",
  "notifications",
  "setup",
  "members",
  "activities",
  "departments",
  "assets",
  "forms",
  "finance",
  // Reserved BEFORE the Global Finance section shipped: without it `/acme/global-finance` parses as a
  // branch named "global-finance" rather than the Organization context, and every page inside
  // resolves against the wrong scope.
  "global-finance",
  "reports",
  "budgets",
  "work-queue",
  // Global search (the phone search screen): without it `/acme/search` in the parish context parses as a
  // branch named "search" and every search request fails to resolve its tenant.
  "search",
  "configuration",
  "people",
  "profile",
  "programmes",
  "roles",
  "my-branch",
  "settings",
  "system",
  "form-designer",
  "report-builder",
])

export function isReservedWorkspaceSlug(slug: string | null | undefined): boolean {
  if (!slug) return false
  return RESERVED_WORKSPACE_SLUGS.has(slug.trim().toLowerCase())
}

/**
 * True when `slug` is an Organization/admin route name rather than a real branch slug. Drives both parser
 * disambiguation and branch-creation validation.
 */
export function isReservedBranchSlug(slug: string | null | undefined): boolean {
  if (!slug) return false
  return RESERVED_BRANCH_SLUGS.has(slug.trim().toLowerCase())
}
